import { Async, SMap } from '@client/new/helpers/types';
import { CreateCouponPayload, DiscountsModel } from '../model/Discounts.model';
import { NewDiscount } from './newDiscount.viewModel';
import { CouponDiscountType, CouponProductType } from 'generated/graphql';
import { createExpirationDate } from './utils';
import { FieldErrors } from 'react-hook-form';

export type Price = [number, number];

export const toPrice = (v: number): Price => {
  const cents = Math.abs(v % 100);
  const dollars = (v - cents) / 100;
  return [dollars, cents];
};

const getOTPPrice = (p: DiscountsModel, otpId?: string) => {
  if (!otpId) return null;
  if (!p.otpPrices[otpId]) return null; // this is a bug
  if (p.otpPrices[otpId].type !== 'fetched') return null;
  return p.otpPrices[otpId].value;
};

export const getCurrentPrice = (
  p: DiscountsModel,
  values: Pick<NewDiscount, 'salesType' | 'subscriptionType' | 'otpId'>,
) => {
  const isSub = values.salesType === 'subscription';
  if (isSub && values.subscriptionType === 'monthly')
    return p.subscriptionMap.monthly?.price || null;
  if (isSub && values.subscriptionType === 'annual')
    return p.subscriptionMap.annual?.price || null;

  const otpPrice = getOTPPrice(p, values.otpId);
  if (values.salesType === 'otp' && otpPrice !== null) return otpPrice;

  return null;
};
type CalculatePricesValues = Pick<
  NewDiscount,
  | 'discountType'
  | 'discountFixedValue'
  | 'discountPercentValue'
  | 'salesType'
  | 'subscriptionType'
  | 'otpId'
>;

export type PricesVM = {
  price: Price;
  priceFloat: number;
  discountedPrice: Price | null;
};
const emptyPrices = { price: null, discountedPrice: null, priceFloat: null };
export type ToPricesVMParams = Pick<
  CalculatePricesValues,
  'discountType' | 'discountFixedValue' | 'discountPercentValue'
>;
export const toPricesVM = (
  price: number,
  values: ToPricesVMParams,
): PricesVM => {
  const { discountType, discountFixedValue, discountPercentValue } = values;
  let discountedPrice: number | null = null;
  if (discountType === 'FixedAmount' && typeof discountFixedValue === 'number')
    discountedPrice =
      discountFixedValue === 0
        ? null
        : parseFloat(price + '') - Math.round(discountFixedValue * 100);

  if (discountType === 'Percentage' && typeof discountPercentValue === 'number')
    discountedPrice =
      discountPercentValue === 0
        ? null
        : parseFloat(price + '') -
          Math.floor((parseFloat(price + '') * discountPercentValue) / 100);

  const priceFloat = parseFloat(price + '');

  return {
    price: toPrice(priceFloat),
    priceFloat: price / 100,
    discountedPrice:
      typeof discountedPrice === 'number' ? toPrice(discountedPrice) : null,
  };
};

// Unit tests purpose only
export const calculatePricesRaw = (
  p: DiscountsModel,
  values: CalculatePricesValues,
) => {
  const price = getCurrentPrice(p, values);
  if (!price) return emptyPrices;
  return toPricesVM(price, values);
};

export const calculatePrices = (
  p: DiscountsModel,
  values: CalculatePricesValues,
): Async<PricesVM> => {
  const value = calculatePricesRaw(p, values);

  if (values.salesType === 'otp') {
    const otpId = values.otpId;
    if (!otpId || p.otpPrices[otpId] === undefined)
      return { type: 'not-fetched' };
    if (p.otpPrices[otpId].type !== 'fetched') return p.otpPrices[otpId];
  }

  if (value.price === null) return { type: 'not-fetched' };

  return { type: 'fetched', value };
};

const characters = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';

const randomChar = () =>
  characters[Math.floor(Math.random() * characters.length)];

export const randomCode = (length: number) =>
  Array.from({ length }, randomChar).join('');

export const mapNewDiscountToCreateCouponVar = (
  discount: NewDiscount,
  subscriptionMap: DiscountsModel['subscriptionMap'],
  influencerId: number,
): CreateCouponPayload => {
  const {
    discountType,
    salesType,
    discountPercentValue,
    discountFixedValue,
    otpType,
  } = discount;

  const productType =
    salesType === 'otp'
      ? otpType === 'bundle'
        ? CouponProductType.OtpBundle
        : CouponProductType.Otp
      : CouponProductType.Subscription;

  const variables: Partial<CreateCouponPayload> = {
    name: discount.name,
    code: discount.discountCode,
    productType,
    discountType: discountType as CouponDiscountType,
    influencerId,
  };

  if (discount.hasExpiration && discount.expiration) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const d = createExpirationDate(discount as any);
    variables.expiresAt = d.toISOString();
  }

  if (discountType === 'FixedAmount' && discountFixedValue)
    // in some cases multiplying by 100 produces a float, so we round it
    variables.discountAmount = Math.round(discountFixedValue * 100);

  if (discountType === 'Percentage' && discountPercentValue)
    variables.percentOff = discountPercentValue;

  if (discount.hasRedemptionsLimit && discount.redemptionsLimit)
    variables.maxRedemptions = discount.redemptionsLimit;

  if (discount.salesType === 'subscription' && discount.subscriptionType) {
    const currentPrice = subscriptionMap[discount.subscriptionType];
    if (currentPrice)
      variables.subscriptionProviderPriceId = currentPrice.providerPriceId;
  }
  if (discount.salesType === 'otp' && discount.otpId)
    variables.paidProductIds = [parseInt(discount.otpId)];

  return variables as CreateCouponPayload;
};

export type ErrorsVM = {
  title: string;
  messages: string[];
} | null;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const toErrors = <T extends SMap<any>>(e: FieldErrors<T>): ErrorsVM => {
  const messages = Object.keys(e || {})
    .map((v) => {
      if (!e[v]?.message && typeof e[v] === 'object')
        return toErrors(e[v] as any)?.messages || [];
      return e[v]?.message;
    })
    .flat()
    .filter(Boolean) as string[];

  if (messages.length === 0) return null;
  return {
    title:
      messages.length > 1 ? 'There are several problems' : 'There is a problem',
    messages,
  };
};
